import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { LeafletMap, WallLayer, useMapInstance } from 'components/Map';
import { useGeolocation } from 'providers';
import { useToast, useLocalStorage } from 'hooks';
import { ActionBtn } from 'pages/games/pinmonkey/ui';
import { WallHUD } from './wall-hud';

const DEFAULT_CENTER = { lat: 41.9168256, lng: -87.6969984 };

const Board = styled.div`
  width: 100vw;
  height: 100vh;
  margin: 0;
  padding: 0;
  position: absolute;
  inset: 0;
`;

const LocateButton = styled(ActionBtn)`
  position: absolute;
  bottom: 1.5rem;
  right: 1rem;
  z-index: 10000;
  pointer-events: auto;
`;

const WallMapController = ({ position, loading, locateRef }) => {
  const map = useMapInstance();
  const pannedRef = useRef(false);

  useEffect(() => {
    if (!map) return;
    locateRef.current = () => {
      if (position?.latitude) map.panTo([position.latitude, position.longitude]);
    };
  }, [map, position?.latitude, position?.longitude]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!map || loading || pannedRef.current || !position?.latitude) return;
    pannedRef.current = true;
    map.panTo([position.latitude, position.longitude]);
  }, [map, loading, position?.latitude, position?.longitude]);

  return null;
};

export const WallGame = () => {
  const [pendingPixels, setPendingPixels] = useLocalStorage('wall:pendingPixels', []);
  const [activeColor, setActiveColor] = useState('#2a2a2a');
  const [wallKey, setWallKey] = useState(0);
  const { showToast, toastElement } = useToast();
  const [isPicking, setIsPicking] = useState(false);
  const [pickedColor, setPickedColor] = useState(null);
  const [isErasing, setIsErasing] = useState(false);
  const activeColorRef = useRef(activeColor);
  const locateRef = useRef(null);
  const { position, loading } = useGeolocation();

  useEffect(() => { activeColorRef.current = activeColor; }, [activeColor]);

  // Group pending pixels by tile key for WallLayer rendering
  const pendingByTile = useMemo(() => {
    const grouped = {};
    pendingPixels.forEach(({ z, x, y, cx, cy, color }) => {
      const key = `${z}/${x}/${y}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({ x: cx, y: cy, color });
    });
    return grouped;
  }, [pendingPixels]);

  const handleCellDraw = useCallback(({ z, x, y, cx, cy }) => {
    setPendingPixels((prev) => {
      const filtered = prev.filter(
        (p) => !(p.z === z && p.x === x && p.y === y && p.cx === cx && p.cy === cy),
      );
      filtered.push({ z, x, y, cx, cy, color: activeColorRef.current });
      return filtered;
    });
  }, []);

  const handlePickColor = useCallback((color) => {
    setIsPicking(false);
    if (color) setPickedColor(color);
  }, []);

  // Removes a pending (unsaved) pixel at this cell, if any — already-saved
  // pixels aren't in pendingPixels so this can never touch committed drawings.
  const handleEraseCell = useCallback(({ z, x, y, cx, cy }) => {
    setPendingPixels((prev) =>
      prev.filter((p) => !(p.z === z && p.x === x && p.y === y && p.cx === cx && p.cy === cy)),
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartPick = useCallback(() => {
    setIsPicking(true);
    setIsErasing(false);
  }, []);

  const handleToggleErase = useCallback(() => {
    setIsErasing((prev) => {
      const next = !prev;
      if (next) setIsPicking(false);
      return next;
    });
  }, []);

  const handleColorChange = useCallback((color) => {
    setActiveColor(color);
    setIsErasing(false);
  }, []);

  const handleSaved = useCallback((lockedCells) => {
    setPendingPixels([]);
    setWallKey((k) => k + 1);
    if (lockedCells?.size) {
      showToast(
        lockedCells.size === 1
          ? "1 pixel couldn't be saved — already drawn in the last 24h"
          : `${lockedCells.size} pixels couldn't be saved — already drawn in the last 24h`,
      );
    }
  }, [showToast]);

  return (
    <Board>
      <LeafletMap center={DEFAULT_CENTER} zoom={18} tileUrl="blank">
        <WallMapController position={position} loading={loading} locateRef={locateRef} />
        <WallLayer
          active={true}
          refreshKey={wallKey}
          pendingByTile={pendingByTile}
          onCellDraw={handleCellDraw}
          userPosition={position}
          isPicking={isPicking}
          onPickColor={handlePickColor}
          isErasing={isErasing}
          onEraseCell={handleEraseCell}
          onOutOfRange={() => showToast('Too far away to draw here — move closer')}
        />
      </LeafletMap>
      <LocateButton onClick={() => locateRef.current?.()} disabled={!position?.latitude}>
        locate
      </LocateButton>
      <WallHUD
        pendingPixels={pendingPixels}
        onColorChange={handleColorChange}
        onSaved={handleSaved}
        onError={showToast}
        userPosition={position}
        isPicking={isPicking}
        onStartPick={handleStartPick}
        pickedColor={pickedColor}
        isErasing={isErasing}
        onToggleErase={handleToggleErase}
      />
      {toastElement}
    </Board>
  );
};
